/**
 * Runtime detection of synthetic monitoring sessions.
 *
 * The status-page monitors in `scripts/monitoring/` and `scripts/playwright/`
 * drive this app with a real browser against real environments every 30
 * minutes, and they load it with:
 *
 *   ?synthetic=1
 *
 * Those runs are scripted, so they land and immediately chat — which pulls any
 * ratio measured against landed users toward 100% and buries real voter
 * behaviour under ~144 scripted sessions a day. `AnalyticsProvider` reads this
 * flag and skips Mixpanel entirely for them.
 *
 * The flag is snapshotted once at module load, and mirrored into sessionStorage
 * because the login check leaves the origin for Auth0's hosted login and comes
 * back to `/callback` on a fresh page load with no query string. Same tab, same
 * sessionStorage, so the opt-out survives the round trip.
 */

const SYNTHETIC_PARAM = 'synthetic';
const SYNTHETIC_STORAGE_KEY = 'votemate_synthetic_session';

const readStoredFlag = (): boolean => {
  try {
    return sessionStorage.getItem(SYNTHETIC_STORAGE_KEY) === 'true';
  } catch {
    // sessionStorage may be unavailable (private mode, sandboxed iframe).
    return false;
  }
};

const persistFlag = (): void => {
  try {
    sessionStorage.setItem(SYNTHETIC_STORAGE_KEY, 'true');
  } catch {
    // sessionStorage may be unavailable; the query param still covers this load.
  }
};

const params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
const paramFlag = params.get(SYNTHETIC_PARAM) === '1' || params.get(SYNTHETIC_PARAM) === 'true';

if (paramFlag) persistFlag();

const syntheticFlag = paramFlag || readStoredFlag();

/**
 * True when this page load belongs to an automated monitoring run rather than a
 * person. Analytics stays off for the whole tab once it is set.
 */
export const isSyntheticSession = (): boolean => syntheticFlag;
